import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Home, Building2, DollarSign, KeyRound, TrendingUp, MapPin, Users, ArrowRight, BookOpen, ExternalLink } from "lucide-react";
import { homesPlace } from "@/lib/api";
import { PropertyCard } from "@/components/homes/PropertyCard";
import { fmtNum, fmtPrice } from "@/components/homes/homesUtil";

export default function HomesPlace() {
  const { geoId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); setD(null); setErr(false);
    homesPlace(geoId).then(setD).catch(() => setErr(true));
  }, [geoId]);

  if (err) return <Center><h1 className="text-3xl font-black text-[#0F172A]">Place not found</h1><Link to="/homes" className="mt-3 text-[#2563EB]">← HomesPulse home</Link></Center>;
  if (!d) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const g = d.geo;
  const s = d.snapshot;
  const scope = (g.geo_type === "city" || g.geo_type === "town") ? `city_geo_id=${g.geo_id}`
    : g.geo_type === "county" ? `county_geo_id=${g.geo_id}`
    : `state_slug=${g.geo_id}`;
  const buyHref = `/homes/search?status=for_sale&${scope}`;
  const rentHref = `/homes/search?status=for_rent&${scope}`;

  return (
    <div data-testid="homes-place">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white">
        {g.image_url && <><img src={g.image_url} alt={g.name} className="absolute inset-0 h-full w-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/80 to-[#0F172A]/40" /></>}
        <div className="relative mx-auto max-w-[1400px] px-4 py-14">
          <nav className="mb-3 flex items-center gap-2 text-sm text-white/60">
            <Link to="/homes" className="hover:text-white">HomesPulse</Link>
            {d.state && <><span>/</span><Link to={`/homes/place/${d.state.geo_id}`} className="hover:text-white">{d.state.name}</Link></>}
          </nav>
          <span className="rounded-full bg-[#2563EB] px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest">{g.geo_type.replace("_", " ")} real estate</span>
          <h1 className="mt-3 text-5xl font-black md:text-6xl">{g.name}</h1>
          {g.nickname && <p className="mt-1 text-lg italic text-white/70">"{g.nickname}"</p>}
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to={buyHref} className="rounded-full bg-[#2563EB] px-5 py-2.5 font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="homes-place-buy">Homes for sale</Link>
            <Link to={rentHref} className="rounded-full border border-white/25 px-5 py-2.5 font-bold text-white transition-colors hover:bg-white/10" data-testid="homes-place-rent">Homes for rent</Link>
          </div>
        </div>
      </section>

      {/* Market snapshot */}
      <section className="mx-auto max-w-[1400px] px-4 py-8">
        <h2 className="mb-4 text-2xl font-black text-[#0F172A]">Market snapshot</h2>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5" data-testid="homes-place-snapshot">
          <Snap icon={DollarSign} label="Median sale price" value={s.median_sale_price ? fmtPrice(s.median_sale_price) : "—"} />
          <Snap icon={KeyRound} label="Median rent" value={s.median_rent ? fmtPrice(s.median_rent, true) : "—"} />
          <Snap icon={Home} label="For sale" value={fmtNum(s.for_sale)} />
          <Snap icon={Building2} label="For rent" value={fmtNum(s.for_rent)} />
          <Snap icon={TrendingUp} label="Avg $/sqft" value={s.avg_price_per_sqft ? `$${s.avg_price_per_sqft}` : "—"} />
        </div>
      </section>

      {/* Community intelligence */}
      {(g.description || g.population != null) && (
        <section className="mx-auto max-w-[1400px] px-4 pb-4">
          <div className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6" data-testid="homes-place-community">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><MapPin className="h-4 w-4" /> Community intelligence</div>
            <h3 className="mt-2 text-2xl font-black text-[#0F172A]">What it's like to live in {g.name}</h3>
            {g.description && <p className="mt-2 max-w-3xl leading-relaxed text-slate-600">{g.description}</p>}
            {g.known_for?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {g.known_for.slice(0, 8).map((k) => <span key={k} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-[#0F172A] ring-1 ring-slate-200">{k}</span>)}
              </div>
            )}
            <div className="mt-5 flex flex-wrap gap-3">
              {g.population != null && <span className="rounded-lg bg-white px-4 py-2 ring-1 ring-slate-200"><span className="block text-[10px] uppercase tracking-wider text-slate-400">Population</span><span className="font-black text-[#0F172A]">{fmtNum(g.population)}</span></span>}
              {g.url_path && <Link to={`/place${g.url_path}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-white" data-testid="homes-place-usapulse">Full community profile ↗</Link>}
              {g.wikipedia?.available && <a href={g.wikipedia.wikipedia_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-white"><BookOpen className="h-4 w-4" /> Wikipedia <ExternalLink className="h-3 w-3" /></a>}
            </div>
          </div>
        </section>
      )}

      {/* Listings */}
      <section className="mx-auto max-w-[1400px] px-4 py-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-black text-[#0F172A]">Latest listings</h2>
          <Link to={buyHref} className="flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:underline">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        {d.listings.length ? (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="homes-place-listings">
            {d.listings.map((l) => <PropertyCard key={l.id} listing={l} />)}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="homes-place-no-listings">
            No live listings here yet — but the community intelligence above is ready. Explore nearby areas below.
          </div>
        )}
      </section>

      {/* Areas */}
      {d.areas?.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-4">
          <h2 className="mb-4 text-2xl font-black text-[#0F172A]">{g.geo_type === "city" || g.geo_type === "town" ? "Nearby cities" : "Cities & areas"}</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="homes-place-areas">
            {d.areas.map((a) => (
              <Link key={a.geo_id} to={`/homes/place/${a.geo_id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-[#2563EB]" data-testid={`homes-area-${a.geo_id}`}>
                <span><span className="block font-semibold text-[#0F172A]">{a.name}</span><span className="text-xs text-slate-500">{fmtNum(a.population)} people</span></span>
                <span className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-xs font-bold text-[#2563EB]">{a.listings}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Agents */}
      {d.agents?.length > 0 && (
        <section className="mx-auto max-w-[1400px] px-4 py-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-2xl font-black text-[#0F172A]">Local agents</h2>
            <Link to={`/homes/agents?state_slug=${d.state?.geo_id || g.geo_id}`} className="flex items-center gap-1.5 text-sm font-semibold text-[#2563EB] hover:underline">All agents <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="homes-place-agents">
            {d.agents.slice(0, 4).map((a) => (
              <Link key={a.id} to={`/homes/agents/${a.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:shadow-md" data-testid={`homes-agent-card-${a.id}`}>
                {a.photo?.thumb_url ? <img src={a.photo.thumb_url} alt={a.name} className="h-12 w-12 rounded-full object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0F172A] text-sm font-black text-white">{a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
                <span className="min-w-0"><span className="block truncate font-bold text-[#0F172A]">{a.name}</span><span className="block truncate text-xs text-slate-500">{a.brokerage}</span></span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
function Snap({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <Icon className="h-5 w-5 text-[#2563EB]" />
      <div className="mt-2 text-2xl font-black text-[#0F172A]">{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}
